// The one money formatter.
//
// Before this module there were three, and they disagreed on screen in a single
// guest session (docs/qa/production-demo-readiness-2026-08-13.md, D3):
//
//   lib/menu/ordering/utils.ts  formatOrderMoney  symbol map + toFixed(2)  -> "₵28.00"
//   lib/pos/shared.ts           formatMoney       Intl, hardcoded en-GH    -> "GH₵28.00"
//   PublicMenuOrderingExperience  fmtAmt          Intl, locale `undefined` -> "GHS 28"
//
// The third was the worst: passing `undefined` as the locale means the *guest's
// own browser* decides how the restaurant's prices look, so the same order
// renders differently on two phones at the same table.
//
// This module imports nothing (constitution R2.3/R3.2) so the server, the
// client and the build-time checkers can all share it.

/** Glyph printed in front of an amount, per ISO 4217 code. */
const CURRENCY_SYMBOLS: Record<string, string> = {
  USD: '$', EUR: '€', GBP: '£', GHS: '₵', NGN: '₦', KES: 'KSh', ZAR: 'R',
  CAD: 'C$', AUD: 'A$', JPY: '¥', CNY: '¥', INR: '₹', BRL: 'R$', EGP: 'E£',
  MXN: 'Mex$', SGD: 'S$', HKD: 'HK$', SEK: 'kr', CHF: 'Fr', TRY: '₺',
  PLN: 'zł', RUB: '₽', AED: 'د.إ', SAR: 'ر.س', XOF: 'CFA', XAF: 'FCFA',
};

/**
 * Currencies with no minor unit. Printing "CFA 15000.00" is wrong the same way
 * multiplying XOF by 100 for Paystack is wrong (see lib/billing/price-book.ts
 * CURRENCY_MINOR_UNIT_EXPONENT) , the sub-unit does not exist.
 */
const ZERO_DECIMAL_CURRENCIES = new Set(['XOF', 'XAF', 'JPY', 'KRW', 'VND', 'CLP', 'ISK', 'RWF', 'UGX']);

export function currencySymbol(currency?: string | null): string {
  if (!currency) return '';
  const trimmed = currency.trim();
  return CURRENCY_SYMBOLS[trimmed.toUpperCase()] || trimmed;
}

export function currencyDecimals(currency?: string | null): number {
  if (!currency) return 2;
  return ZERO_DECIMAL_CURRENCIES.has(currency.trim().toUpperCase()) ? 0 : 2;
}

/**
 * Format an amount for display. Deterministic: the output depends only on the
 * amount and the currency, never on the viewer's locale.
 *
 * formatMoney(28, 'GHS')     -> "₵28.00"
 * formatMoney(15000, 'XOF')  -> "CFA 15,000"
 * formatMoney(12.5, 'USD')   -> "$12.50"
 */
export function formatMoney(value: number | null | undefined, currency?: string | null): string {
  const amount = Number(value);
  const safe = Number.isFinite(amount) ? amount : 0;
  const decimals = currencyDecimals(currency);
  const symbol = currencySymbol(currency);

  // Group thousands with a plain comma rather than a locale separator, so a
  // French-locale phone does not render "1 234,00" against an English menu.
  const fixed = safe.toFixed(decimals);
  const [whole, fraction] = fixed.split('.');
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const body = fraction ? `${grouped}.${fraction}` : grouped;

  // Multi-letter codes read better with a space ("CFA 15,000", "KSh 400");
  // single glyphs sit tight against the number ("₵28.00").
  if (!symbol) return body;
  return symbol.length > 1 ? `${symbol} ${body}` : `${symbol}${body}`;
}
